import { ArrowDown2, Stop } from '@/components/icons';
import { optionLabel, resolveFieldType } from '../builderUtils';
import { DEFAULT_RATING_CONFIG, MAX_RATING_VALUES } from '../constants';
import type { SurveyQuestion } from '@/api/surveys';

interface QuestionPreviewProps {
    question: SurveyQuestion;
}

const inputLabelByType: Record<string, string> = {
    number: 'Resposta numérica',
    email: 'nome@exemplo.com',
    date: 'dd/mm/aaaa',
    cpf: '000.000.000-00',
    phone: '(00) 00000-0000',
};

const getRatingValues = (question: SurveyQuestion) => {
    const rating = question.config?.rating ?? DEFAULT_RATING_CONFIG;
    const values: number[] = [];

    const step = rating.step > 0 ? rating.step : 1;

    for (
        let value = rating.min;
        value <= rating.max && values.length < MAX_RATING_VALUES;
        value += step
    ) {
        values.push(value);
    }

    return values.length ? values : [1, 2, 3, 4, 5];
};

export function QuestionPreview({ question }: QuestionPreviewProps) {
    const fieldType = resolveFieldType(question);
    const options = question.options ?? [];

    const renderControl = () => {
        if (fieldType === 'textarea') {
            return (
                <div className="h-24 rounded-xl border border-dashed border-border bg-background/70 px-3 py-2 text-sm text-muted-foreground">
                    {question.config?.placeholder || 'Resposta longa'}
                </div>
            );
        }

        if (fieldType === 'select') {
            return (
                <div className="space-y-3">
                    <div className="flex h-10 items-center justify-between rounded-xl border border-border bg-background/70 px-3 text-sm text-muted-foreground">
                        {question.config?.placeholder || 'Selecione uma opção'}
                        <ArrowDown2 className="size-4" />
                    </div>
                    {options.length ? (
                        <div className="flex flex-wrap gap-2">
                            {options.slice(0, 4).map((option, index) => (
                                <span
                                    key={`${optionLabel(option)}-${index}`}
                                    className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground"
                                >
                                    {optionLabel(option)}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
            );
        }

        if (fieldType === 'multiselect') {
            return (
                <div className="space-y-2">
                    {(options.length ? options : ['Opção 1', 'Opção 2'])
                        .slice(0, 4)
                        .map((option, index) => (
                            <div
                                key={`${typeof option === 'string' ? option : option.label}-${index}`}
                                className="flex items-center gap-2 rounded-xl border border-border bg-background/70 px-3 py-2 text-sm text-muted-foreground"
                            >
                                <Stop className="size-3.5" />
                                {typeof option === 'string' ? option : option.label}
                            </div>
                        ))}
                </div>
            );
        }

        if (fieldType === 'rating') {
            const rating = question.config?.rating ?? DEFAULT_RATING_CONFIG;

            return (
                <div className="inline-flex max-w-full flex-col gap-2">
                    <div className="flex flex-wrap gap-2">
                        {getRatingValues(question).map((value) => (
                            <span
                                key={value}
                                className="flex h-9 min-w-9 items-center justify-center rounded-xl border border-border bg-background px-3 text-sm font-medium text-muted-foreground"
                            >
                                {value}
                            </span>
                        ))}
                    </div>
                    {rating.left_label || rating.right_label ? (
                        <div className="flex w-full justify-between gap-4 text-xs text-muted-foreground">
                            <span>{rating.left_label}</span>
                            <span className="text-right">{rating.right_label}</span>
                        </div>
                    ) : null}
                </div>
            );
        }

        return (
            <div className="flex h-10 items-center rounded-xl border border-dashed border-border bg-background/70 px-3 text-sm text-muted-foreground">
                {question.config?.placeholder || inputLabelByType[fieldType] || 'Resposta curta'}
            </div>
        );
    };

    return (
        <div className="space-y-3">
            {question.config?.help_text ? (
                <p className="text-xs leading-relaxed text-muted-foreground">
                    {question.config.help_text}
                </p>
            ) : null}
            {renderControl()}
        </div>
    );
}
